import { useState } from "react";
import { ArrowUpRight, Download } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import BookingDialog from "@/components/BookingDialog";
import { catalogUrl } from "@/lib/api";

const SERVICES = [
  {
    key: "wholesale_quote",
    n: "01",
    name: "Request a Wholesale Quote",
    body: "Stock Capstone in your shop. Tell us about your business and we'll prepare a tailored trade quotation for the collection.",
  },
  {
    key: "order_samples",
    n: "02",
    name: "Request Order Samples",
    body: "Feel the cloth before you commit. We'll arrange samples of our tweeds, tartans and waxed cottons for your consideration.",
  },
  {
    key: "catalog_pdf",
    n: "03",
    name: "Download Catalog PDF",
    body: "Receive our current catalogue — the full collection, materials and trade information. We'll email it to you after your request.",
  },
];

export default function Wholesale() {
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="wholesale-page" className="pt-20">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow className="mb-6">Est. United Kingdom — Wholesale</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight" data-testid="wholesale-headline">
              Our <span className="italic text-green">Services</span>
            </h1>
          </Reveal>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <Reveal delay={0.2}>
            <p className="text-smoke leading-relaxed max-w-md">
              Partner with Capstone. Three ways to begin — each a short,
              complimentary call with our team. Choose a time that suits
              you and we'll handle the rest.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
        <div className="border-y border-line divide-y divide-line" data-testid="services-list">
          {SERVICES.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.08}>
              <div className="group grid md:grid-cols-12 gap-6 items-center py-12 md:py-16">
                <p className="md:col-span-1 font-serif text-3xl text-line group-hover:text-honey transition-colors duration-500">
                  {s.n}
                </p>
                <div className="md:col-span-5">
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight group-hover:italic transition-all duration-500">
                    {s.name}
                  </h2>
                  <p className="eyebrow !text-[0.6rem] text-honey mt-4" data-testid={`service-meta-${s.key}`}>
                    15 min — Free
                  </p>
                </div>
                <p className="md:col-span-4 text-sm text-smoke leading-relaxed">{s.body}</p>
                <div className="md:col-span-2 md:text-right">
                  <button
                    data-testid={`book-now-${s.key}`}
                    onClick={() => setSelected(s)}
                    className="group/btn inline-flex items-center gap-2 border border-ink eyebrow !text-[0.625rem] px-7 py-4 hover:bg-green hover:border-green hover:text-ivory transition-colors duration-300"
                  >
                    Book Now
                    <ArrowUpRight size={13} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20 bg-green text-ivory p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow className="!text-honey mb-4">The catalogue</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="catalog-band-title">
              Prefer to browse first?
            </h2>
            <p className="mt-4 text-ivory/70 leading-relaxed max-w-lg text-sm">
              Our trade catalogue holds the full collection — every cloth,
              every cut, and the story behind each. Yours to keep.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={catalogUrl}
              data-testid="catalog-download-button"
              className="group inline-flex items-center gap-3 bg-ivory text-green eyebrow !text-[0.625rem] px-8 py-5 hover:bg-honey hover:text-ivory transition-colors duration-300"
            >
              <Download size={14} className="group-hover:translate-y-0.5 transition-transform duration-300" />
              Download catalogue PDF
            </a>
          </div>
        </Reveal>
      </section>

      {selected && <BookingDialog service={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
